import { PrivacyBlur } from "#components/privacy-blur"
import { useTheme } from "#components/theme-provider"
import { Button } from "#components/ui/button"
import { Card, CardContent, CardTitle } from "#components/ui/card"
import { Body, StatLabel, StatValue } from "#components/ui/typography"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import { useThemeColors } from "#hooks/use-theme-colors"
import { cardEntrance, getCardHoverEffect, statValue } from "#lib/animations"
import type { DashboardAnalyticsResponse } from "@home-finance-manager/shared/types/dashboard"
import { AnimatePresence, motion } from "framer-motion"
import { List, PieChart as PieChartIcon } from "lucide-react"
import React, { useState } from "react"
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"

interface ExpenseSourcesCardProps {
  analytics: DashboardAnalyticsResponse | undefined
}

export const ExpenseSourcesCard = React.memo<ExpenseSourcesCardProps>(({ analytics }) => {
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const themeColors = useThemeColors()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const [viewMode, setViewMode] = useState<"chart" | "list">("chart")

  // Get the most recent month's data (last in the array, as they're sorted chronologically)
  const selectedMonthIndex = React.useMemo(() => {
    if (!analytics?.expenseSourcesByMonth || analytics.expenseSourcesByMonth.length === 0) return -1
    return analytics.expenseSourcesByMonth.length - 1
  }, [analytics])

  const selectedMonthData =
    selectedMonthIndex >= 0 ? analytics?.expenseSourcesByMonth?.[selectedMonthIndex] || null : null
  const expenseSources = selectedMonthData?.expenseSources || []
  const totalExpenses = selectedMonthData?.totalExpenses || 0

  const pieData = expenseSources.map((source) => ({
    name: source.categoryName,
    value: source.amount,
    color: source.color,
    percentage: source.percentage,
  }))

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      className="h-full min-h-0 flex flex-col"
    >
      <Card className="flex flex-col overflow-hidden h-full max-h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-sm dark:shadow-none dark:border-border/50 transition-shadow hover:shadow-md dark:hover:shadow-none">
        {/* Accent border */}
        <div
          className={`h-1 absolute top-0 left-0 right-0 ${
            isDark
              ? "bg-gradient-to-r from-primary/60 via-primary/40 to-transparent"
              : "bg-gradient-to-r from-primary/80 via-primary/60 to-transparent"
          }`}
        />
        <CardContent className="p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 flex-shrink-0 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CardTitle className="text-base font-medium text-foreground">
                  {t("components.dashboard.expenseSources.title")}
                </CardTitle>
                {/* View Toggle Button */}
                <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-md border border-border/50">
                  <Button
                    variant={viewMode === "chart" ? "default" : "ghost"}
                    size="icon-sm"
                    onClick={() => setViewMode("chart")}
                    className="h-7 w-7"
                    aria-label="Chart view"
                  >
                    <PieChartIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "default" : "ghost"}
                    size="icon-sm"
                    onClick={() => setViewMode("list")}
                    className="h-7 w-7"
                    aria-label="List view"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <motion.div variants={statValue}>
                <StatValue className="text-3xl sm:text-4xl mb-2">
                  {formatCurrency(totalExpenses)}
                </StatValue>
              </motion.div>
              <StatLabel>{t("components.dashboard.expenseSources.subtitle")}</StatLabel>
            </div>
          </div>

          {/* Content Area - Chart or List */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <AnimatePresence mode="wait">
              {viewMode === "chart" ? (
                <motion.div
                  key="chart"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="h-full flex items-center justify-center"
                >
                  {pieData.length > 0 ? (
                    <div className="w-full h-full max-w-md max-h-full flex items-center justify-center relative overflow-visible">
                      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={120}
                            dataKey="value"
                            stroke="none"
                          >
                            {pieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.color}
                                stroke="none"
                                style={{
                                  filter: isDark
                                    ? "drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
                                    : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                                }}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload || !payload.length) return null

                              const data = payload[0].payload as {
                                name: string
                                value: number
                                percentage: number
                              }

                              return (
                                <div
                                  className="rounded-md border bg-card px-3 py-2 text-sm shadow-md"
                                  style={{
                                    backgroundColor: themeColors.card,
                                    borderColor: themeColors.border,
                                    color: themeColors.cardForeground,
                                    zIndex: 9999,
                                    position: "relative",
                                    minWidth: "fit-content",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <div className="font-medium">{data.name}</div>
                                  <div className="text-xs">
                                    {data.percentage.toFixed(0)}% :{" "}
                                    <PrivacyBlur>{formatCurrency(data.value)}</PrivacyBlur>
                                  </div>
                                </div>
                              )
                            }}
                            wrapperStyle={{
                              zIndex: 9999,
                              pointerEvents: "none",
                            }}
                            allowEscapeViewBox={{
                              x: true,
                              y: true,
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      {/* Center value */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                          <div className="text-sm text-muted-foreground font-medium">
                            {t("components.dashboard.expenseSources.total")}
                          </div>
                          <div className="text-lg font-bold text-foreground">
                            <PrivacyBlur>{formatCurrency(totalExpenses)}</PrivacyBlur>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full max-w-md max-h-full bg-muted/10 rounded-full flex items-center justify-center border border-border/50">
                      <Body className="text-muted-foreground text-sm">
                        {t("components.dashboard.expenseSources.noData")}
                      </Body>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="list"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="max-h-[303px] overflow-y-auto space-y-0 pr-1"
                >
                  {expenseSources.length > 0 ? (
                    expenseSources.map((source, index) => (
                      <div
                        key={source.categoryId}
                        className="relative flex items-center justify-between text-sm group hover:bg-muted/30 rounded-md px-3 py-2 transition-colors flex-shrink-0 border border-transparent hover:border-border/50"
                      >
                        {index < expenseSources.length - 1 && (
                          <div
                            className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
                            style={{
                              background: `linear-gradient(to right, transparent 0%, var(--border) 20%, var(--border) 80%, transparent 100%)`,
                              opacity: 0.5,
                            }}
                          />
                        )}
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-3 h-3 rounded-full shadow-sm flex-shrink-0"
                            style={{
                              backgroundColor: source.color,
                              boxShadow: `0 0 8px ${source.color}40`,
                            }}
                          />
                          <span className="text-foreground font-medium truncate">
                            {source.categoryName}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <span className="text-muted-foreground text-xs">
                            <PrivacyBlur>{formatCurrency(source.amount)}</PrivacyBlur>
                          </span>
                          <span className="font-semibold text-foreground min-w-[3rem] text-right">
                            {source.percentage.toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-8">
                      {t("components.dashboard.expenseSources.noDataAvailable")}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
})

ExpenseSourcesCard.displayName = "ExpenseSourcesCard"
